import ApiError from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";
import ApiResponseV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponseV2";

const FALLBACK_ERROR = "An internal error occurred, please try again later";

function isAxiosError(error: unknown): error is { response?: { status?: number; data?: unknown } } {
  return typeof error === "object" && error !== null && (error as { isAxiosError?: boolean }).isAxiosError === true;
}

function apiErrorFromRejection(error: unknown): ApiError<unknown> {
  const response = isAxiosError(error) ? error.response : undefined;
  const data = response?.data as { message?: unknown; errorCode?: unknown } | undefined;
  const message = typeof data?.message === "string" && data.message ? data.message : FALLBACK_ERROR;
  const errorCode = typeof data?.errorCode === "string" && data.errorCode ? data.errorCode : "GENERIC_ERROR";
  const apiResponse = ApiResponseV2.fromAxiosResponse<unknown>({
    status: response?.status ?? 500,
    data: {
      success: false,
      content: null,
      errorCode,
      message,
    },
  } as never);
  return new ApiError(errorCode, message, apiResponse);
}

/** Keep a caller-facing message and still hand MutationCache an ApiError it can toast. */
export function rethrowWithMessage(error: unknown, message: string): never {
  const apiError = error instanceof ApiError ? error : apiErrorFromRejection(error);
  throw new ApiError(apiError.errorCode, message, apiError.apiResponse);
}

/** Keep a specific server message. Use fallback only when the failure has no text of its own. */
export function rethrowWithFallback(error: unknown, fallback: string): never {
  if (error instanceof ApiError && error.message && error.message !== FALLBACK_ERROR) {
    throw error;
  }
  return rethrowWithMessage(error, fallback);
}

/**
 * Turns an HttpClientV2 envelope into a Promise that React Query can fail.
 * Without this, every query reports success even on a 500 because HttpClientV2 never rejects.
 */
export function unwrap<T>(p: Promise<ApiResponseV2<T>>): Promise<T> {
  return p
    .then((response) => response.getContentOrThrowError())
    .catch((error: unknown) => {
      if (error instanceof ApiError) throw error;
      if (isAxiosError(error)) throw apiErrorFromRejection(error);
      throw error;
    });
}
